import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiBook } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import './Courses.css';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ program: '', semester: '', tahunAjaran: '', lecturer: '' });
  const [programs, setPrograms] = useState([]);
  const [tahunAjaranList, setTahunAjaranList] = useState([]);
  const [lecturersList, setLecturersList] = useState([]);
  const [stats, setStats] = useState({ duplicateCodes: 0, withoutLecturer: 0 });
  const { user } = useAuth();

  useEffect(() => {
    fetchPrograms();
    fetchLecturers();
    fetchStats();
  }, []);

  const fetchPrograms = async () => {
    try {
      const res = await axios.get('/api/courses');
      // Get unique programs from courses
      const uniquePrograms = [...new Set(res.data.data.map(c => c.program))].filter(p => p);
      setPrograms(uniquePrograms.sort());
      
      // Get unique tahun ajaran from courses
      const uniqueTahunAjaran = [...new Set(res.data.data.map(c => c.tahunAjaran))].filter(t => t);
      setTahunAjaranList(uniqueTahunAjaran.sort().reverse());
    } catch (error) {
      console.error('Gagal mengambil data program');
    }
  };

  const fetchLecturers = async () => {
    try {
      const res = await axios.get('/api/courses/lecturers/list');
      setLecturersList(res.data.data);
    } catch (error) {
      console.error('Gagal mengambil daftar dosen');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/courses/stats');
      setStats(res.data.data);
    } catch (error) {
      console.error('Gagal mengambil statistik mata kuliah');
    }
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(filter).toString();
      const res = await axios.get(`/api/courses?${queryParams}`);
      setCourses(res.data.data);
    } catch (error) {
      toast.error('Gagal mengambil data mata kuliah');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus mata kuliah ini?')) {
      try {
        await axios.delete(`/api/courses/${id}`);
        toast.success('Mata kuliah berhasil dihapus');
        fetchCourses();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Gagal menghapus mata kuliah');
      }
    }
  };

  const canEdit = user?.role === 'admin' || user?.role === 'staff';
  const canDelete = user?.role === 'admin';

  return (
    <div className="courses-page">
      {/* Modern Transparent Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiBook className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Mata Kuliah</h1>
              <p className="header-subtitle">Kelola dan monitor data mata kuliah</p>
            </div>
          </div>
          {canEdit && (
            <div className="header-actions">
              <Link to="/courses/new" className="btn btn-glass btn-primary">
                <FiPlus /> 
                <span className="btn-text">Tambah Mata Kuliah</span>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-cards-grid">
        <div className="stat-card stat-card-warning">
          <div className="stat-icon">
            <FiBook />
          </div>
          <div className="stat-content">
            <h3>{stats.duplicateCodes}</h3>
            <p>Kode MK Sama Beda Nama</p>
          </div>
        </div>

        <div className="stat-card stat-card-danger">
          <div className="stat-icon">
            <FiBook />
          </div>
          <div className="stat-content">
            <h3>{stats.withoutLecturer}</h3>
            <p>Mata Kuliah Tanpa Dosen</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card filters-card">
        <div className="filters-grid">
          <div className="form-group">
            <select
              value={filter.program}
              onChange={(e) => setFilter({ ...filter, program: e.target.value })}
              className="form-control"
            >
              <option value="">Semua Program Studi</option>
              {programs.map(program => (
                <option key={program} value={program}>{program}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <select
              value={filter.semester}
              onChange={(e) => setFilter({ ...filter, semester: e.target.value })}
              className="form-control"
            >
              <option value="">Semua Semester</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(sem => (
                <option key={sem} value={sem}>Semester {sem}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <select
              value={filter.tahunAjaran}
              onChange={(e) => setFilter({ ...filter, tahunAjaran: e.target.value })}
              className="form-control"
            >
              <option value="">Semua Tahun Ajaran</option>
              {tahunAjaranList.map(tahun => (
                <option key={tahun} value={tahun}>{tahun}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <select
              value={filter.lecturer}
              onChange={(e) => setFilter({ ...filter, lecturer: e.target.value })}
              className="form-control"
            >
              <option value="">Semua Dosen</option>
              {lecturersList.map(lecturer => (
                <option key={lecturer} value={lecturer}>{lecturer}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="courses-grid">
        {loading ? (
          <div className="spinner"></div>
        ) : courses.length === 0 ? (
          <div className="empty-state">
            <FiBook size={48} />
            <p>Tidak ada data mata kuliah</p>
          </div>
        ) : (
          courses.map((course) => (
            <div key={course._id} className="course-card">
              <div className="course-header">
                <div className="course-code">{course.code}</div>
                <div className="course-actions">
                  {canEdit && (
                    <Link to={`/courses/edit/${course._id}`} className="btn-icon" title="Edit">
                      <FiEdit />
                    </Link>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(course._id)}
                      className="btn-icon btn-icon-danger"
                      title="Hapus"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              </div>

              <h3>{course.name}</h3>
              
              <div className="course-info">
                <div className="info-row">
                  <span className="label">Dosen:</span>
                  <span>{course.lecturer}</span>
                </div>
                <div className="info-row">
                  <span className="label">Program:</span>
                  <span>{course.program}</span>
                </div>
                <div className="info-row">
                  <span className="label">Semester:</span>
                  <span>{course.semester}</span>
                </div>
                <div className="info-row">
                  <span className="label">Tahun Ajaran:</span>
                  <span>{course.tahunAjaran}</span>
                </div>
                <div className="info-row">
                  <span className="label">SKS:</span>
                  <span className="badge badge-info">{course.credits} SKS</span>
                </div>
              </div>

              {/* Dosen Pengajar Detail */}
              {course.lecturers && course.lecturers.length > 0 && (
                <div className="lecturers-section">
                  <strong>Dosen Pengajar ({course.lecturers.length}):</strong>
                  <ul className="lecturers-list">
                    {course.lecturers.map((lec, idx) => (
                      <li key={idx} className="lecturer-item">
                        <div className="lecturer-name">
                          {lec.nama_dosen}
                          {lec.nidn && <span className="nidn"> (NIDN: {lec.nidn})</span>}
                        </div>
                        <div className="lecturer-details">
                          {lec.sks_tatap_muka > 0 && (
                            <span className="detail-badge">SKS: {lec.sks_tatap_muka}</span>
                          )}
                          {lec.rencana_minggu_pertemuan > 0 && (
                            <span className="detail-badge">Pertemuan: {lec.rencana_minggu_pertemuan}</span>
                          )}
                          {lec.nama_jenis_evaluasi && (
                            <span className="detail-badge">{lec.nama_jenis_evaluasi}</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {course.schedule && course.schedule.day && (
                <div className="course-schedule">
                  <strong>Jadwal:</strong> {course.schedule.day}, {course.schedule.startTime} - {course.schedule.endTime}
                  <br />
                  <strong>Ruang:</strong> {course.schedule.room}
                </div>
              )}

              {course.description && (
                <p className="course-description">{course.description}</p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Courses;
